import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { analyzeSign, type SignedInterval } from '../../../analytics';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { MonotonicityParams } from './monotonicity.model';

/**
 * Creates monotonicity visualization for functions.
 *
 * Shows where a function is increasing (f'(x) > 0) or decreasing (f'(x) < 0)
 * using colored background polygons and/or segmented curves.
 *
 * Features:
 * - Background polygons: Large colored rectangles behind the curve
 * - Segmented curves: Multiple curve segments with different colors (optional)
 *
 * The visualization is reactive and updates when:
 * - The viewport (bounding box) changes
 * - The parent function parameters change
 *
 * Performance optimization:
 * - Intervals are cached and only recalculated when the board updates
 * - Uses fewer scan samples for smoother dragging
 */
export class MonotonicityCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'monotonicity';

  /** Maximum number of intervals to display */
  private readonly maxIntervals = 20;

  /** Height of polygons (very large to cover any viewport) */
  private readonly polygonHeight = 10000;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, featureTheme } = context;
    const params = def.params as MonotonicityParams | undefined;

    if (!params?.of) {
      throw new Error('Monotonicity feature requires params.of to specify the parent function');
    }

    const parentFn = this.getParentFunction(params.of, context);
    const objects: GeometryElement[] = [];

    // Resolve styles for polygons and curves at the root level (for visibility)
    const rootPolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, []);
    const rootCurveStyle = resolveFeatureStyle('curve', this.featureType, context, display, []);

    // Determine whether to show polygons/curves based on their visibility in the resolved style
    const showPolygons = rootPolygonStyle.visible !== false;
    const showSegments = rootCurveStyle.visible === true;

    // Resolve styles for increasing and decreasing regions
    const increasingPolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['increasing']);
    const decreasingPolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['decreasing']);
    const increasingCurveStyle = resolveFeatureStyle('curve', this.featureType, context, display, ['increasing']);
    const decreasingCurveStyle = resolveFeatureStyle('curve', this.featureType, context, display, ['decreasing']);

    const increasingVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['increasing']);
    const decreasingVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['decreasing']);

    // =========================================================================
    // PERFORMANCE OPTIMIZATION: Cache interval calculations per render frame
    // =========================================================================
    // Instead of recalculating intervals on every attribute function call,
    // we cache them and only update once per board update cycle.
    // The cache is invalidated when:
    // - The bounding box changes (viewport pan/zoom)
    // - The function parameters change (slider/handle drag)
    let cachedIntervals: SignedInterval[] = [];
    let cacheKey = '';

    const getIntervals = (): SignedInterval[] => {
      const bbox = board.getBoundingBox();
      const fnParams = parentFn.getParams();
      // Create a cache key from bounding box AND function parameters
      const paramsKey = Object.values(fnParams)
        .map((v) => v.toFixed(4))
        .join(',');
      const newKey = `${bbox[0].toFixed(2)},${bbox[2].toFixed(2)}|${paramsKey}`;

      if (newKey !== cacheKey) {
        cacheKey = newKey;
        const derivative = parentFn.getDerivative(1);
        // Use fewer samples for faster calculation during drag
        cachedIntervals = analyzeSign(derivative, bbox[0], bbox[2], {
          maxResults: this.maxIntervals,
          scanSamples: 50, // Reduced from default 200 for better performance
        });
      }
      return cachedIntervals;
    };

    const getInterval = (index: number): SignedInterval | null => {
      const intervals = getIntervals();
      return index < intervals.length ? intervals[index] : null;
    };

    // =========================================================================
    // Create placeholder polygons and curves
    // =========================================================================

    if (showPolygons) {
      for (let i = 0; i < this.maxIntervals; i++) {
        const index = i;

        // Create vertices as functions for reactivity
        const polygon = board.create(
          'polygon',
          [
            // Bottom-left
            [() => getInterval(index)?.start ?? NaN, -this.polygonHeight],
            // Bottom-right
            [() => getInterval(index)?.end ?? NaN, -this.polygonHeight],
            // Top-right
            [() => getInterval(index)?.end ?? NaN, this.polygonHeight],
            // Top-left
            [() => getInterval(index)?.start ?? NaN, this.polygonHeight],
          ],
          {
            withLabel: false,
            vertices: { visible: false },
            borders: { visible: false },
            fillColor: () => {
              const interval = getInterval(index);
              if (!interval) {
                return 'transparent';
              }
              const style = interval.sign === 'positive' ? increasingPolygonStyle : decreasingPolygonStyle;
              return style.fillColor ? context.themeService.getColor(style.fillColor) : 'transparent';
            },
            fillOpacity: () => {
              const interval = getInterval(index);
              if (!interval) {
                return 0;
              }
              const style = interval.sign === 'positive' ? increasingPolygonStyle : decreasingPolygonStyle;
              return style.fillOpacity ?? 0.15;
            },
            strokeWidth: 0,
            visible: () => {
              const interval = getInterval(index);
              if (!interval) {
                return false;
              }
              return interval.sign === 'positive' ? increasingVisible : decreasingVisible;
            },
            layer: 0, // Render behind curves (default layer is 7)
          },
        ) as GeometryElement;

        objects.push(polygon);
      }
    }

    if (showSegments) {
      for (let i = 0; i < this.maxIntervals; i++) {
        const index = i;

        // Create curve segment using board.create directly for reactive domain support
        // The function returns NaN outside its interval, effectively clipping it
        const curve = board.create(
          'functiongraph',
          [
            (x: number) => {
              const interval = getInterval(index);
              if (!interval) {
                return NaN;
              }
              // Only evaluate within the interval bounds
              if (x < interval.start || x > interval.end) {
                return NaN;
              }
              return parentFn.evaluate(x);
            },
          ],
          {
            strokeColor: () => {
              const interval = getInterval(index);
              if (!interval) {
                return 'transparent';
              }
              const style = interval.sign === 'positive' ? increasingCurveStyle : decreasingCurveStyle;
              return style.color ? context.themeService.getColor(style.color) : 'transparent';
            },
            strokeWidth: () => {
              const interval = getInterval(index);
              if (!interval) {
                return 2;
              }
              return interval.sign === 'positive'
                ? increasingCurveStyle.strokeWidth ?? 3
                : decreasingCurveStyle.strokeWidth ?? 3;
            },
            visible: () => {
              const interval = getInterval(index);
              if (!interval) {
                return false;
              }
              return interval.sign === 'positive' ? increasingVisible : decreasingVisible;
            },
            withLabel: false,
          },
        ) as GeometryElement;

        objects.push(curve);
      }
    }

    return {
      id,
      type: 'monotonicity',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
